import React, { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';

const emptyOption = null;

const safeJson = (value) => {
   if (!value || (Array.isArray(value) && value.length === 0)) {
      return null;
   }

   if (typeof value === 'object') {
      return JSON.stringify(value, null, 2);
   }

   return String(value);
};

const valueFrom = (value, key) => {
   if (!value || typeof value !== 'object') {
      return null;
   }

   return value[key] ?? null;
};

const actorText = (log) => {
   if (log.actor_name && log.actor_email) {
      return `${log.actor_name} <${log.actor_email}>`;
   }

   return log.actor_name || log.actor_email || 'Sistema';
};

const documentText = (log) => log.document_title || 'documento sin nombre';
const dossierText = (log) => log.dossier_title || 'expediente sin nombre';
const companyText = (log) => log.provider_company_name || log.provider_common_name || log.company_name || 'empresa sin nombre';
const clientText = (log) => log.company_name || log.company_common_name || null;

const dossierUrl = (log) => {
   if (log.person_provider_id) {
      return `/provider/dossier/${log.person_provider_id}`;
   }

   if (log.dossier_id) {
      return `/dossiers/${log.dossier_id}/edit`;
   }

   return null;
};

const documentUrl = (log) => log.document_id ? `/documents/${log.document_id}/edit` : null;

const eventConfig = (eventType) => ({
   'user.logged_in': { label: 'Inicio de sesión', color: 'secondary' },
   'document.requested': { label: 'Documento solicitado', color: 'info' },
   'document.uploaded': { label: 'Documento cargado', color: 'primary' },
   'document.approved': { label: 'Documento aprobado', color: 'success' },
   'document.rejected': { label: 'Documento rechazado', color: 'danger' },
   'document.expired': { label: 'Documento histórico/caducado', color: 'warning' },
   'document.pending_signature': { label: 'Pendiente de firma', color: 'warning' },
   'document.deleted': { label: 'Documento borrado', color: 'danger' },
   'document.restored': { label: 'Documento restaurado', color: 'success' },
   'dossier.status_changed': { label: 'Status cambiado', color: 'warning' },
   'dossier.deleted': { label: 'Expediente borrado', color: 'danger' },
   'dossier.replaced': { label: 'Expediente reemplazado', color: 'primary' },
   'dossier.document_replaced': { label: 'Documento reemplazado en expediente', color: 'primary' },
   'dossier_template.updated': { label: 'Plantilla actualizada', color: 'info' },
   'dossier_template.deleted': { label: 'Plantilla borrada', color: 'danger' },
   'dossier_template.document_replaced': { label: 'Documento reemplazado en plantilla', color: 'primary' },
   'document_recurrence.suspended': { label: 'Recurrencia suspendida', color: 'warning' },
   'document_recurrence.reactivated': { label: 'Recurrencia reactivada', color: 'success' },
   'document_periods.generated': { label: 'Históricos generados', color: 'success' },
   'document_periods.deleted': { label: 'Históricos borrados', color: 'danger' },
}[eventType] || { label: eventType, color: 'secondary' });

const humanEventName = (eventType) => eventConfig(eventType).label;

const groupLabel = (eventGroup) => ({
   user: 'Usuario',
   document: 'Documento',
   dossier: 'Expediente',
   dossier_template: 'Plantilla de expediente',
   document_recurrence: 'Recurrencia',
   document_periods: 'Históricos',
   invitation: 'Invitación',
}[eventGroup] || eventGroup);

const renderEventBadge = (log) => {
   const config = eventConfig(log.event_type);

   return (
      <>
         <span className={`badge badge-${config.color} mr-2 mb-1`}>{config.label}</span>
         <span className="badge badge-light-dark mr-2 mb-1">{groupLabel(log.event_group)}</span>
      </>
   );
};

const renderDossierLink = (log) => {
   const url = dossierUrl(log);

   if (!url) {
      return <strong>{dossierText(log)}</strong>;
   }

   return (
      <a href={url} target="_blank" rel="noreferrer" className="font-weight-bolder text-primary">
         {dossierText(log)}
      </a>
   );
};

const renderDocumentLink = (log) => {
   const url = documentUrl(log);

   if (!url) {
      return <strong>{documentText(log)}</strong>;
   }

   return (
      <a href={url} target="_blank" rel="noreferrer" className="font-weight-bolder text-primary">
         {documentText(log)}
      </a>
   );
};

const renderAuditSentence = (log) => {
   const actor = <strong>{actorText(log)}</strong>;
   const document = renderDocumentLink(log);
   const dossier = renderDossierLink(log);
   const company = <strong>{companyText(log)}</strong>;
   const oldStatus = valueFrom(log.old_values, 'status_name');
   const newStatus = valueFrom(log.new_values, 'status_name');
   const oldDossier = valueFrom(log.old_values, 'dossier_title');
   const newDossier = valueFrom(log.new_values, 'dossier_title');
   const oldDocumentId = valueFrom(log.old_values, 'document_id');
   const newDocumentId = valueFrom(log.new_values, 'document_id');
   const from = valueFrom(log.metadata, 'from');
   const to = valueFrom(log.metadata, 'to');

   switch (log.event_type) {
      case 'user.logged_in':
         return <>{actor} inició sesión.</>;
      case 'document.requested':
         return <>{actor} solicitó el documento {document} en el expediente {dossier} de {company}.</>;
      case 'document.uploaded':
         return <>{actor} subió el documento {document} al expediente {dossier} de {company}.</>;
      case 'document.approved':
         return <>{actor} aprobó el documento {document} del expediente {dossier} de {company}.</>;
      case 'document.rejected':
         return <>{actor} rechazó el documento {document} del expediente {dossier} de {company}.</>;
      case 'document.deleted':
         return <>{actor} borró el documento {document} del expediente {dossier} de {company}.</>;
      case 'document.restored':
         return <>{actor} restauró el documento {document} del expediente {dossier} de {company}.</>;
      case 'document.expired':
         return <>{actor} marcó como histórico/caducado el documento {document} del expediente {dossier} de {company}.</>;
      case 'document.pending_signature':
         return <>{actor} dejó pendiente de firma el documento {document} del expediente {dossier} de {company}.</>;
      case 'dossier.status_changed':
         return <>{actor} cambió el status del expediente {dossier} de {company}{oldStatus || newStatus ? <> de <strong>{oldStatus || 'sin status'}</strong> a <strong>{newStatus || 'sin status'}</strong></> : null}.</>;
      case 'dossier.deleted':
         return <>{actor} borró el expediente {dossier} de {company}.</>;
      case 'dossier.replaced':
         return <>{actor} reemplazó el expediente de <strong>{oldDossier || 'plantilla anterior'}</strong> a <strong>{newDossier || dossierText(log)}</strong> para {company}. {renderDossierLink(log)}</>;
      case 'dossier.document_replaced':
         return <>{actor} reemplazó un documento en el expediente {dossier} de {company}: documento #{oldDocumentId || 'anterior'} por documento #{newDocumentId || 'nuevo'}.</>;
      case 'dossier_template.updated':
         return <>{actor} actualizó la plantilla de expediente {dossier} de {company}.</>;
      case 'dossier_template.deleted':
         return <>{actor} borró la plantilla de expediente {dossier} de {company}.</>;
      case 'dossier_template.document_replaced':
         return <>{actor} reemplazó un documento en la plantilla {dossier} de {company}: documento #{oldDocumentId || 'anterior'} por documento #{newDocumentId || 'nuevo'}.</>;
      case 'document_recurrence.suspended':
         return <>{actor} suspendió la recurrencia de documentos en el expediente {dossier} de {company}.</>;
      case 'document_recurrence.reactivated':
         return <>{actor} reactivó la recurrencia de documentos en el expediente {dossier} de {company}.</>;
      case 'document_periods.generated':
         return <>{actor} generó solicitudes de documentos históricos en el expediente {dossier} de {company}{from || to ? <> para el periodo <strong>{from || '?'}</strong> a <strong>{to || '?'}</strong></> : null}.</>;
      case 'document_periods.deleted':
         return <>{actor} borró solicitudes de documentos históricos en el expediente {dossier} de {company}{from || to ? <> para el periodo <strong>{from || '?'}</strong> a <strong>{to || '?'}</strong></> : null}.</>;
      default:
         return <>{actor} realizó la acción <strong>{humanEventName(log.event_type)}</strong>{log.document_title ? <> sobre el documento {document}</> : null}{log.dossier_title ? <> en el expediente {dossier}</> : null}{log.company_name ? <> de {company}</> : null}.</>;
   }
};

const AdministratorLogs = () => {
   const [companies, setCompanies] = useState([]);
   const [dossiers, setDossiers] = useState([]);
   const [documents, setDocuments] = useState([]);
   const [users, setUsers] = useState([]);

   const [selectedCompany, setSelectedCompany] = useState(emptyOption);
   const [selectedDossier, setSelectedDossier] = useState(emptyOption);
   const [selectedDocument, setSelectedDocument] = useState(emptyOption);
   const [selectedUser, setSelectedUser] = useState(emptyOption);
   const [userEmail, setUserEmail] = useState('');
   const [dateFrom, setDateFrom] = useState('');
   const [dateTo, setDateTo] = useState('');

   const [logs, setLogs] = useState([]);
   const [loadingFilters, setLoadingFilters] = useState(false);
   const [loadingLogs, setLoadingLogs] = useState(false);
   const [message, setMessage] = useState('Selecciona una empresa para comenzar.');

   const selectStyles = useMemo(() => ({
      menu: (provided) => ({ ...provided, zIndex: 20 }),
   }), []);

   useEffect(() => {
      loadCompanies();
      loadUsers();
   }, []);

   const loadCompanies = (search = '') => {
      axios.get('/administrator/logs/companies', { params: { search } })
         .then((res) => setCompanies(res.data || []));
   };

   const loadUsers = (search = '', company = selectedCompany) => {
      axios.get('/administrator/logs/users', {
         params: {
            search,
            company_id: company?.value || undefined,
         }
      }).then((res) => setUsers(res.data || []));
   };

   const loadCompanyChildren = (company) => {
      setSelectedCompany(company);
      setSelectedDossier(emptyOption);
      setSelectedDocument(emptyOption);
      setSelectedUser(emptyOption);
      setDocuments([]);
      setLogs([]);

      if (!company) {
         setDossiers([]);
         loadUsers('', emptyOption);
         setMessage('Selecciona una empresa para comenzar.');
         return;
      }

      setLoadingFilters(true);
      setMessage('Selecciona expediente, documento o usuario y presiona Mostrar logs.');

      Promise.all([
         axios.get('/administrator/logs/dossiers', { params: { company_id: company.value } }),
         axios.get('/administrator/logs/users', { params: { company_id: company.value } }),
      ]).then(([dossiersRes, usersRes]) => {
         setDossiers(dossiersRes.data || []);
         setUsers(usersRes.data || []);
      }).finally(() => setLoadingFilters(false));
   };

   const loadDocuments = (dossier) => {
      setSelectedDossier(dossier);
      setSelectedDocument(emptyOption);
      setLogs([]);

      if (!dossier) {
         setDocuments([]);
         return;
      }

      setLoadingFilters(true);
      axios.get('/administrator/logs/documents', { params: { person_provider_id: dossier.value } })
         .then((res) => setDocuments(res.data || []))
         .finally(() => setLoadingFilters(false));
   };

   const showLogs = () => {
      if (!selectedCompany && !selectedDossier && !selectedDocument && !selectedUser && !userEmail && !dateFrom && !dateTo) {
         setMessage('Selecciona empresa, expediente, documento, usuario, correo o rango de fechas antes de mostrar logs.');
         return;
      }

      setLoadingLogs(true);
      setMessage('Cargando logs...');

      axios.get('/administrator/logs/events', {
         params: {
            company_id: selectedCompany?.value || undefined,
            person_provider_id: selectedDossier?.value || undefined,
            person_document_id: selectedDocument?.value || undefined,
            user_id: selectedUser?.value || undefined,
            user_email: userEmail || undefined,
            date_from: dateFrom || undefined,
            date_to: dateTo || undefined,
         }
      }).then((res) => {
         const data = res.data?.data || [];
         setLogs(data);
         setMessage(data.length ? `Se encontraron ${data.length} eventos.` : 'No se encontraron logs con esos filtros.');
      }).catch(() => {
         setMessage('No se pudieron cargar los logs.');
      }).finally(() => setLoadingLogs(false));
   };

   const clearFilters = () => {
      setSelectedCompany(emptyOption);
      setSelectedDossier(emptyOption);
      setSelectedDocument(emptyOption);
      setSelectedUser(emptyOption);
      setUserEmail('');
      setDateFrom('');
      setDateTo('');
      setDossiers([]);
      setDocuments([]);
      loadUsers('', emptyOption);
      setLogs([]);
      setMessage('Selecciona una empresa para comenzar.');
   };

   return (
      <div className="administrator-logs">
         <div className="d-flex justify-content-between align-items-start flex-wrap mb-5">
            <div>
               <h2 className="mb-2">Logs y trazabilidad</h2>
               <p className="text-muted mb-0">
                  Filtros escalonados por empresa, expediente, documento y usuario.
               </p>
            </div>
            <a href="/administrator" className="btn btn-light-primary mt-3 mt-md-0">
               Volver a empresas
            </a>
         </div>

         <div className="card mb-5">
            <div className="card-body">
               <div className="row">
                  <div className="col-lg-6 mb-4">
                     <label className="mb-1 text-dark-50 font-weight-bold">Empresa</label>
                     <Select
                        value={selectedCompany}
                        onChange={loadCompanyChildren}
                        onInputChange={(value) => loadCompanies(value)}
                        options={companies}
                        placeholder="Escribe para buscar empresa por razón social o RFC"
                        isClearable
                        isSearchable
                        styles={selectStyles}
                     />
                  </div>

                  <div className="col-lg-6 mb-4">
                     <label className="mb-1 text-dark-50 font-weight-bold">Expediente</label>
                     <Select
                        value={selectedDossier}
                        onChange={loadDocuments}
                        options={dossiers}
                        placeholder={selectedCompany ? 'Selecciona expediente/proveedor' : 'Primero selecciona empresa'}
                        isDisabled={!selectedCompany || loadingFilters}
                        isClearable
                        isSearchable
                        styles={selectStyles}
                     />
                  </div>

                  <div className="col-lg-6 mb-4">
                     <label className="mb-1 text-dark-50 font-weight-bold">Documento de expediente</label>
                     <Select
                        value={selectedDocument}
                        onChange={setSelectedDocument}
                        options={documents}
                        placeholder={selectedDossier ? 'Selecciona documento' : 'Primero selecciona expediente'}
                        isDisabled={!selectedDossier || loadingFilters}
                        isClearable
                        isSearchable
                        styles={selectStyles}
                     />
                  </div>

                  <div className="col-lg-6 mb-4">
                     <label className="mb-1 text-dark-50 font-weight-bold">Usuario opcional</label>
                     <Select
                        value={selectedUser}
                        onChange={setSelectedUser}
                        onInputChange={(value) => loadUsers(value)}
                        options={users}
                        placeholder="Escribe nombre o correo, sin elegir empresa"
                        isDisabled={loadingFilters}
                        isClearable
                        isSearchable
                        styles={selectStyles}
                     />
                  </div>

                  <div className="col-lg-6 mb-4">
                     <label className="mb-1 text-dark-50 font-weight-bold">Correo de usuario</label>
                     <input
                        type="email"
                        className="form-control"
                        value={userEmail}
                        onChange={(event) => setUserEmail(event.target.value)}
                        placeholder="portal.proveedores@gda.mx"
                     />
                     <span className="form-text text-muted">Puedes poner solo el correo para ver todo lo que hizo ese usuario.</span>
                  </div>

                  <div className="col-lg-3 mb-4">
                     <label className="mb-1 text-dark-50 font-weight-bold">Desde</label>
                     <input
                        type="date"
                        className="form-control"
                        value={dateFrom}
                        onChange={(event) => setDateFrom(event.target.value)}
                     />
                  </div>

                  <div className="col-lg-3 mb-4">
                     <label className="mb-1 text-dark-50 font-weight-bold">Hasta</label>
                     <input
                        type="date"
                        className="form-control"
                        value={dateTo}
                        onChange={(event) => setDateTo(event.target.value)}
                     />
                  </div>
               </div>

               <div className="d-flex align-items-center flex-wrap">
                  <button type="button" className="btn btn-primary mr-3 mb-2" onClick={showLogs} disabled={loadingLogs || loadingFilters}>
                     {loadingLogs ? 'Cargando...' : 'Mostrar logs'}
                  </button>
                  <button type="button" className="btn btn-light mb-2" onClick={clearFilters} disabled={loadingLogs || loadingFilters}>
                     Limpiar filtros
                  </button>
                  <span className="text-muted ml-md-4 mb-2">{message}</span>
               </div>
            </div>
         </div>

         <div className="card">
            <div className="card-body">
               <h4 className="mb-4">Resultados</h4>
               {logs.length === 0 ? (
                  <div className="alert alert-secondary mb-0">
                     No hay logs para mostrar todavía.
                  </div>
               ) : (
                  <div className="timeline timeline-6 mt-3">
                     {logs.map((log) => {
                        const oldValues = safeJson(log.old_values);
                        const newValues = safeJson(log.new_values);
                        const metadata = safeJson(log.metadata);

                        return (
                           <div className="timeline-item align-items-start" key={log.id}>
                              <div className="timeline-label font-weight-bolder text-dark-75 font-size-sm">
                                 {log.occurred_at}
                              </div>
                              <div className="timeline-badge">
                                 <i className="fa fa-genderless text-primary icon-xl"></i>
                              </div>
                              <div className="timeline-content pl-3 mb-8">
                                 <div className="d-flex justify-content-between flex-wrap align-items-center mb-2">
                                    <div>
                                       {renderEventBadge(log)}
                                    </div>
                                    <span className="text-muted">#{log.id}</span>
                                 </div>

                                 <div className="alert alert-light-primary mb-3 py-3">
                                    <div className="font-size-lg text-dark-75">
                                       {renderAuditSentence(log)}
                                    </div>
                                 </div>

                                 <div className="text-muted font-size-sm mb-2">
                                    {companyText(log) ? <>Empresa del expediente: <strong>{companyText(log)}</strong></> : null}
                                    {clientText(log) && clientText(log) !== companyText(log) ? <> | Cliente/owner: <strong>{clientText(log)}</strong></> : null}
                                    {(companyText(log) || clientText(log)) && log.dossier_title ? ' | ' : ''}
                                    {log.dossier_title ? <>Expediente: {renderDossierLink(log)}</> : null}
                                    {log.dossier_title && log.document_title ? ' | ' : ''}
                                    {log.document_title ? <>Documento: {renderDocumentLink(log)}</> : null}
                                 </div>

                                 {(oldValues || newValues || metadata) && (
                                    <details className="mb-3">
                                       <summary className="text-primary font-weight-bold cursor-pointer">Ver datos técnicos</summary>
                                       <div className="row mt-3">
                                          <div className="col-lg-12 mb-3">
                                             <strong>Código interno</strong>
                                             <div className="bg-light p-3 rounded small">
                                                Evento: {log.event_type} | Grupo: {log.event_group}
                                             </div>
                                          </div>
                                          {oldValues && (
                                             <div className="col-lg-4 mb-3">
                                                <strong>Antes</strong>
                                                <pre className="bg-light p-3 rounded small mb-0">{oldValues}</pre>
                                             </div>
                                          )}
                                          {newValues && (
                                             <div className="col-lg-4 mb-3">
                                                <strong>Después</strong>
                                                <pre className="bg-light p-3 rounded small mb-0">{newValues}</pre>
                                             </div>
                                          )}
                                          {metadata && (
                                             <div className="col-lg-4 mb-3">
                                                <strong>Metadata</strong>
                                                <pre className="bg-light p-3 rounded small mb-0">{metadata}</pre>
                                             </div>
                                          )}
                                       </div>
                                    </details>
                                 )}
                              </div>
                           </div>
                        );
                     })}
                  </div>
               )}
            </div>
         </div>
      </div>
   );
};

export default AdministratorLogs;
